import React, { useCallback, useEffect, useRef } from 'react';
import { motion, useMotionTemplate, useMotionValue } from 'framer-motion';

/**
 * Magic UI Magic Card (21st.dev / @dillionverma) — spotlight border + hover wash.
 * Light theme only; brand teal gradient. MIT — see LICENSE-magicui.md.
 */
const MagicCard = ({
  children,
  className = '',
  gradientSize = 220,
  gradientColor = '#ecfeff',
  gradientOpacity = 0.75,
  gradientFrom = '#99f6e4',
  gradientTo = '#0891b2'
}) => {
  const mouseX = useMotionValue(-gradientSize);
  const mouseY = useMotionValue(-gradientSize);
  const sizeRef = useRef(gradientSize);

  useEffect(() => {
    sizeRef.current = gradientSize;
  }, [gradientSize]);

  const reset = useCallback(() => {
    const off = -sizeRef.current;
    mouseX.set(off);
    mouseY.set(off);
  }, [mouseX, mouseY]);

  useEffect(() => {
    reset();
  }, [reset]);

  const onMove = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      mouseX.set(e.clientX - rect.left);
      mouseY.set(e.clientY - rect.top);
    },
    [mouseX, mouseY]
  );

  const borderBg = useMotionTemplate`linear-gradient(#fff 0 0) padding-box, radial-gradient(${gradientSize}px circle at ${mouseX}px ${mouseY}px, ${gradientFrom}, ${gradientTo}, var(--color-border, #d5e5ea) 100%) border-box`;
  const washBg = useMotionTemplate`radial-gradient(${gradientSize}px circle at ${mouseX}px ${mouseY}px, ${gradientColor}, transparent 100%)`;

  return (
    <motion.div
      className={`magic-card ${className}`.trim()}
      onPointerMove={onMove}
      onPointerLeave={reset}
      style={{ background: borderBg }}
    >
      <div className="magic-card__plate" aria-hidden="true" />
      <motion.div
        className="magic-card__wash"
        aria-hidden="true"
        style={{ background: washBg, '--wash-opacity': gradientOpacity }}
      />
      <div className="magic-card__body">{children}</div>
    </motion.div>
  );
};

export default MagicCard;
